// The notification feed, shared by every surface that shows it.
//
// A MODULE-LEVEL SINGLETON with subscribers, deliberately — the same shape as
// useRole. Three things render this data at once (the bell, its badge, and the
// chat panel's greeting), and three independent hooks would mean three polls on
// the same interval, three different answers mid-flight, and a badge that says 3
// while the list shows 4. One store, one poll, one truth.

import { useCallback, useEffect, useState } from 'react'
import {
  listNotifications,
  markNotificationsRead,
  dismissNotifications,
  type NotificationRecord,
} from '@/lib/api'
import { useRole } from '@/hooks/useRole'
import { applyAppBadge } from '@/lib/appBadge'

export interface NotificationState {
  items: NotificationRecord[]
  unread: number
  status: 'idle' | 'loading' | 'ready' | 'error'
}

const EMPTY: NotificationState = { items: [], unread: 0, status: 'idle' }

let _state: NotificationState = EMPTY
const _listeners = new Set<(s: NotificationState) => void>()
let _timer: ReturnType<typeof setInterval> | null = null
let _inFlight = false

function publish(next: NotificationState) {
  const before = _state.unread
  _state = next
  for (const fn of _listeners) fn(next)
  // The app icon follows the SAME number the bell shows, written from the one
  // place that owns it. Putting this in publish() rather than in each caller is
  // what guarantees the icon and the badge can never disagree — every path that
  // changes the count (the poll, markRead, dismiss, dismissAll) goes through
  // here, and a future one gets it for free.
  //
  // Skipped when the count did not move, because on iOS every call is real work
  // and the poll fires on a timer whether or not anything happened.
  if (next.unread !== before) void applyAppBadge(next.unread)
}

// How many the bell and the panel hold. Enough to be useful, bounded because
// this is a table that grows one row per event per person and a phone should
// never be asked to hold all of it.
const PAGE = 30

async function refresh(): Promise<void> {
  // A slow response must not stack up behind the interval — on a poor connection
  // that turns a 30-second poll into a queue of overlapping requests that answer
  // out of order, and the newest result is not necessarily the last to land.
  if (_inFlight) return
  _inFlight = true
  try {
    const page = await listNotifications({ limit: PAGE })
    // Shape-checked rather than trusted. This feature is ambient — a bell and a
    // greeting — and it renders INSIDE the operator's chat panel and screen
    // header, so a malformed payload must degrade to "nothing waiting" rather
    // than throwing through those trees. A notification feed that can white-screen
    // the app it decorates is a bad trade at any reliability.
    const items = Array.isArray(page?.notifications) ? page.notifications : []
    const unread = typeof page?.unread === 'number' ? page.unread : 0
    publish({ items, unread, status: 'ready' })
  } catch {
    // Keep whatever is on screen. A failed poll is not evidence the
    // notifications went away, and blanking the list on a dropped request is how
    // a bell flickers empty on a train.
    publish({ ..._state, status: 'error' })
  } finally {
    _inFlight = false
  }
}

/**
 * @param intervalMs polling cadence. The bell is ambient, so callers pass
 *   something slow; there is one timer regardless of how many components mount.
 */
export function useNotifications(intervalMs = 60000): NotificationState & {
  refresh: () => Promise<void>
  markRead: (ids: string[]) => Promise<void>
  dismiss: (ids: string[]) => Promise<void>
  dismissAll: () => Promise<void>
} {
  const { status: roleStatus, userRole } = useRole()
  const [state, setState] = useState<NotificationState>(_state)

  // Only for a resolved, signed-in person. Polling before the role lands would
  // fire a request with no tenant header and get a 401 on every tick.
  const active = roleStatus === 'resolved' && userRole !== 'unknown'

  useEffect(() => {
    _listeners.add(setState)
    return () => { _listeners.delete(setState) }
  }, [])

  useEffect(() => {
    if (!active) return
    if (_state.status === 'idle') {
      publish({ ..._state, status: 'loading' })
      void refresh()
    }
    if (_timer === null) {
      _timer = setInterval(() => { void refresh() }, intervalMs)
    }
    return () => {
      // The timer is torn down only when the LAST consumer unmounts — with one
      // subscriber left this hook is still the one holding it.
      if (_listeners.size <= 1 && _timer !== null) {
        clearInterval(_timer)
        _timer = null
      }
    }
  }, [active, intervalMs])

  // Optimistic, then reconciled by the next poll. Waiting for the round trip to
  // clear a badge makes dismissing feel broken on a slow connection; the server
  // is still the authority and a failure simply leaves the next refresh to
  // correct it.
  const markRead = useCallback(async (ids: string[]) => {
    if (ids.length === 0) return
    const seen = new Set(ids)
    const stamp = new Date().toISOString()
    publish({
      ..._state,
      items: _state.items.map((n) => (seen.has(n.id) && !n.read_at ? { ...n, read_at: stamp } : n)),
      unread: Math.max(0, _state.unread - _state.items.filter((n) => seen.has(n.id) && !n.read_at).length),
    })
    try { await markNotificationsRead({ ids }) } finally { void refresh() }
  }, [])

  const dismiss = useCallback(async (ids: string[]) => {
    if (ids.length === 0) return
    const seen = new Set(ids)
    publish({
      ..._state,
      items: _state.items.filter((n) => !seen.has(n.id)),
      unread: Math.max(0, _state.unread - _state.items.filter((n) => seen.has(n.id) && !n.read_at).length),
    })
    try { await dismissNotifications({ ids }) } finally { void refresh() }
  }, [])

  const dismissAll = useCallback(async () => {
    publish({ ..._state, items: [], unread: 0 })
    try { await dismissNotifications({ all: true }) } finally { void refresh() }
  }, [])

  return { ...state, refresh, markRead, dismiss, dismissAll }
}

/** Clears the store on sign-out, so the next person never sees the last one's inbox. */
export function resetNotifications(): void {
  if (_timer !== null) { clearInterval(_timer); _timer = null }
  publish(EMPTY)
  // Unconditionally, NOT via publish's changed-count check: signing out of an
  // account that already had zero unread still has to wipe an icon badge left
  // by the previous session. The store's count and the icon are only in step
  // while somebody is signed in.
  void applyAppBadge(0)
}
